<template>
  <v-card 
    flat
    class="rounded-lg ma-1"
  >
    <v-card-title class="d-flex align-center">
      Financeiro - Requisições
    </v-card-title>

    <v-card-text>
      <v-tabs
        v-model="tab"
        align-tabs="center"
        color="red-darken-4"
      >
        <v-tab :value="1">Aprovadas</v-tab>
        <v-tab :value="2">Negadas</v-tab>
      </v-tabs>

      <div class="d-flex justify-end py-4 px-2 ga-2">
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          density="compact"
          label="Procurar"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </div>
      
      <v-window v-model="tab">
        <!-- APPROVED -->
        <v-window-item :value="1">
          <v-data-table 
            v-model:search="search" 
            :items="approvedRequestsAsOptions"
            :headers="headers"
          >
            <template v-slot:[`item.priority`]="{ item }">
              <v-chip :color="priorityColor(item)">
                {{ priorityLabel(item) }}
              </v-chip>
            </template>
            <template v-slot:[`item.payWarning`]="{ item }">
              <v-chip :color="item.payWarning === 'Sim' ? 'error' : 'success'">
                {{ item.payWarning }}
              </v-chip>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-pencil"
                variant="plain"
                size="small"
                @click="editRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-eye"
                variant="plain"
                size="small"
                @click="viewRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-check-all"
                variant="plain"
                size="small"
                @click="completeRequest(item)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-window-item>
        
        <v-window-item :value="2">
          <v-data-table 
            v-model:search="search" 
            :items="deniedRequests"
            :headers="headers"
          >
            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-eye"
                variant="plain"
                size="small"
                @click="viewRequest(item)"
              ></v-btn> 
            </template>
          </v-data-table>
        </v-window-item>
      </v-window>
    </v-card-text>

    <EditRequestDialog 
      v-model="editDialog"
      :request="selectedRequest"
      @close="editDialog = false"
      @save="handleUpdateRequest"
    />

    <RequestDetailsDialog 
      v-model="detailsDialog"
      :request="selectedRequest"
      @snackbar="snackbarHandler"
      @close="detailsDialog = false"
    />
    
    <CompleteRequestDialog 
      v-model="completeDialog"
      :request="selectedRequest"
      @close="completeDialog = false"
      @complete="handleComplete"
    />
    
    <v-snackbar
      v-model="snackbar.active"
      :timeout="4000"
      :color="snackbar.color"
      elevation="24"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import RequestDetailsDialog from '@/components/Request/RequestDetailsDialog.vue';
import CompleteRequestDialog from '@/components/Request/CompleteRequestDialog.vue';
import EditRequestDialog from '@/components/Request/EditRequestDialog.vue';

import { REQUEST_STATUS, PRIORITY_TYPES, PAYMENT_WARNING_METHOD_ID } from '@/helpers/constants/app.constants';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'FinanceContent',

  components: { 
    RequestDetailsDialog,
    CompleteRequestDialog,
    EditRequestDialog
  },
  
  data: () => ({
    tab: 1,
    search: '',
    detailsDialog: false,
    completeDialog: false,
    editDialog: false,
    selectedRequest: null,
    snackbar: {
      text: '',
      color: '',
      active: false
    }
  }),

  computed: {
    ...mapGetters('request', [
      'approvedRequests',
      'deniedRequests'
    ]),

    approvedRequestsAsOptions() {
      return this.approvedRequests.map(request => ({
        ...request,
        payWarning: this.payWarningLabel(request)
      }));
    },

    headers() {
      return [
        { title: 'Código', key: 'code' },
        { title: 'Descrição', key: 'description' },
        { title: 'Requisitado', key: 'requestDate' },
        { title: 'À Vista', key: 'payWarning' },
        { title: 'Prioridade', key: 'priority' },
        { title: 'Ações', key: 'action', sortable: false }
      ]
    },
  },

  methods: {
    ...mapActions('request', ['updateRequest']),

    payWarningLabel(item) {
      console.log(item);
      const payWarning = item.requestOptions.filter(option => option.paymentMethodId === PAYMENT_WARNING_METHOD_ID);
      return payWarning.length ? 'Sim' : 'Não';
    },

    priorityLabel(item) {
      return PRIORITY_TYPES[item.priority].label;
    },

    priorityColor(item) {
      return PRIORITY_TYPES[item.priority].color;
    },

    editRequest(item) {
      this.selectedRequest = item;
      this.editDialog = true;
    },

    viewRequest(item) {
      this.selectedRequest = item;
      this.detailsDialog = true;
    },
    
    completeRequest(item) {
      this.selectedRequest = item;
      this.completeDialog = true;
    },

    async handleUpdateRequest(requestData) {
      const { success, error } = await this.updateRequest(requestData);
      
      if (success) {
        this.snackbarHandler({ color: 'success', text: 'Requisição atualizada com sucesso!' });
      } else {
        this.snackbarHandler({ color: 'error', text: `Erro ao atualizar requisição: ${error}` });
      }
    },
    
    async handleComplete(invoiceNumber, comments) {
      const { success, error } = await this.updateRequest({
        ...this.selectedRequest,
        status: REQUEST_STATUS.COMPLETED.id,
        note: `${this.selectedRequest.note ? `${this.selectedRequest.note} - ` : ''}Nota Fiscal: ${invoiceNumber}. ${comments}`
      });
      
      if (success) {
        this.snackbarHandler({ color: 'success', text: 'Requisição finalizada com sucesso!' });
      } else {
        this.snackbarHandler({ color: 'error', text: `Erro ao finalizar requisição: ${error}` });
      }
    },
    
    snackbarHandler({color, text}) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.active = true;
    }
  }
}
</script> 